/**
 * `/leaderboards` — ranked agents by Glicko rating.
 *
 * Reads the latest bench tournament from `/replays/index.json` and
 * folds the standings into LeaderboardRow shape via `leaderboardFromTournament`.
 */
import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  LeaderboardTable,
  type LeaderboardRow,
} from "@pilot-protocol/ui";
import {
  leaderboardFromTournament,
  loadBenchIndex,
  loadTournament,
  type BenchIndexEntry,
} from "../data/bench";

interface Loaded {
  benches: ReadonlyArray<BenchIndexEntry>;
  activeBench: BenchIndexEntry;
  rows: LeaderboardRow[];
}

export default function LeaderboardsPage() {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const index = await loadBenchIndex();
        const active = index.tournaments[0];
        if (!active) throw new Error("no tournaments in replays/index.json");
        const tournament = await loadTournament(active.id);
        if (cancelled) return;
        setLoaded({
          benches: index.tournaments,
          activeBench: active,
          rows: leaderboardFromTournament(tournament),
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={pageStyle}>
      <header style={headStyle}>
        <Badge variant="outline">Leaderboard</Badge>
        <h1 style={titleStyle}>Top pilots</h1>
        <p style={leadStyle}>
          Glicko-2 ratings from the latest bench tournament. Drill into a
          tournament for the matches that produced these numbers.
        </p>
        {loaded ? (
          <p style={metaStyle}>
            Source: {loaded.activeBench.name}
            {loaded.activeBench.startedAtUnixMs
              ? ` · ${new Date(loaded.activeBench.startedAtUnixMs).toLocaleDateString()}`
              : ""}
          </p>
        ) : null}
        <div style={{ display: "flex", gap: 12 }}>
          <Link to="/tournaments" style={{ textDecoration: "none" }}>
            <Button variant="outline" size="sm">
              Browse tournaments
            </Button>
          </Link>
        </div>
      </header>

      {error ? (
        <p style={errorStyle}>Failed to load leaderboard: {error}</p>
      ) : loaded ? (
        <LeaderboardTable rows={loaded.rows} />
      ) : (
        <LeaderboardTable rows={[]} loading />
      )}
    </div>
  );
}

const pageStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 24,
};

const headStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  alignItems: "flex-start",
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-display)",
  fontSize: "var(--text-3xl)",
};

const leadStyle: CSSProperties = {
  margin: 0,
  maxWidth: 640,
  color: "var(--foreground-secondary)",
  fontSize: "var(--text-base)",
  lineHeight: 1.5,
};

const metaStyle: CSSProperties = {
  margin: 0,
  fontSize: "var(--text-xs)",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--foreground-muted)",
};

const errorStyle: CSSProperties = {
  margin: 0,
  color: "var(--destructive)",
};
